import styles from './IntakePattern.module.css';

export type IntakePatternId = 'constant' | 'varying' | 'seasonal' | 'growing' | 'bursts' | 'capped';

export interface IntakeState {
  pattern: IntakePatternId;
  quietPct: number;
  swingPct: number;
  peakMonth: number;
  startMonth: number;
  rampStartPct: number;
  rampMonths: number;
  burstMonths: number;
  burstEvery: number;
  storageCapGb: number;
  deleteAfterMonths: number;
}

export const DEFAULT_INTAKE: IntakeState = {
  pattern: 'constant', quietPct: 50, swingPct: 50, peakMonth: 7, startMonth: 1, rampStartPct: 20, rampMonths: 6,
  burstMonths: 2, burstEvery: 6, storageCapGb: 300, deleteAfterMonths: 0,
};

const PATTERNS: { id: IntakePatternId; label: string; hint: string }[] = [
  { id: 'constant', label: 'Constant', hint: 'The table above arrives every month.' },
  {
    id: 'varying', label: 'Varying',
    hint: 'Some months are busier than others. The table above is the busiest month; the results use the average month and show the range from every month quiet to every month busy.',
  },
  {
    id: 'seasonal', label: 'Seasonal',
    hint: 'Survey season: the table above is an average month, uploads peak in the busiest month and dip six months later.',
  },
  { id: 'growing', label: 'Growing', hint: 'A new customer ramping up: uploads start at a share of the table above and grow to it in a straight line.' },
  { id: 'bursts', label: 'Project bursts', hint: 'The table above arrives in busy months only, with nothing in between.' },
  {
    id: 'capped', label: 'Up to a storage limit',
    hint: 'Uploads stop once the originals reach the limit, as a plan quota stops them. Deleting old files frees room for new ones.',
  },
];

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

/** What the server needs; whole numbers where the server expects months. */
export const intakeInput = (s: IntakeState) => ({
  pattern: s.pattern,
  quietPct: Math.min(100, Math.max(0, s.quietPct || 0)),
  swingPct: Math.min(100, Math.max(0, s.swingPct || 0)),
  peakMonth: s.peakMonth,
  startMonth: s.startMonth,
  rampStartPct: Math.min(100, Math.max(0, s.rampStartPct || 0)),
  rampMonths: Math.max(2, Math.round(s.rampMonths || 2)),
  burstMonths: Math.max(1, Math.round(s.burstMonths || 1)),
  burstEvery: Math.max(1, Math.round(s.burstEvery || 1)),
  storageCapGb: Math.max(0, s.storageCapGb || 0),
  deleteAfterMonths: Math.max(0, Math.round(s.deleteAfterMonths || 0)),
});

const gbLabel = (gb: number) => (gb >= 1024 ? `${+(gb / 1024).toFixed(1)} TB` : `${Math.round(gb)} GB`);

// Uploads per month as bars; for varying, the busiest month as a faint bar
// and the quietest as a tick, around the average the results use.
const IntakeStrip = ({ weights, quiet }: { weights: number[]; quiet: number | null }) => {
  const top = Math.max(1, ...weights);
  const pctOf = (w: number) => `${Math.round(w * 100)}%`;
  const lo = Math.min(...weights);
  const hi = Math.max(...weights);
  const summary = quiet != null
    ? `average ${pctOf(weights[0] ?? 0)} of the table, between ${pctOf(quiet)} and 100%`
    : lo === hi ? `${pctOf(lo)} of the table every month` : `from ${pctOf(lo)} to ${pctOf(hi)} of the table`;
  return (
    <figure className={styles.strip}>
      <div className={styles.bars} role="img" aria-label={`Uploads per month over ${weights.length} months: ${summary}.`}>
        {weights.map((w, i) => (
          <div key={i} className={styles.slot} title={`Month ${i + 1}: ${pctOf(w)} of the table`}>
            {quiet != null && <div className={styles.busy} style={{ height: `${(1 / top) * 100}%` }} />}
            <div className={styles.bar} style={{ height: `${(w / top) * 100}%` }} />
            {quiet != null && <div className={styles.quiet} style={{ bottom: `${(quiet / top) * 100}%` }} />}
          </div>
        ))}
      </div>
      <figcaption className={styles.caption}>
        Uploads per month, month 1 to {weights.length}: {summary}.
      </figcaption>
    </figure>
  );
};

interface Props {
  value: IntakeState;
  onChange: (next: IntakeState) => void;
  /** The weights the last calculation used (1 = the table). */
  weights?: number[];
  quietPct?: number | null;
  /** Storage quotas offered as one-click limits (admins only). */
  plans?: { name: string; gb: number }[];
}

const IntakePattern = ({ value, onChange, weights, quietPct = null, plans = [] }: Props) => {
  const set = (patch: Partial<IntakeState>) => onChange({ ...value, ...patch });
  const numField = (key: keyof IntakeState, label: string, suffix: string, opts: { min?: number; max?: number; step?: number } = {}) => (
    <label className={styles.field}>
      <span>{label}</span>
      <input
        className={styles.input}
        type="number"
        min={opts.min ?? 0}
        max={opts.max}
        step={opts.step ?? 1}
        value={value[key] as number}
        onChange={(e) => set({ [key]: Number(e.target.value) } as Partial<IntakeState>)}
      />
      <span className={styles.suffix}>{suffix}</span>
    </label>
  );
  const monthField = (key: 'peakMonth' | 'startMonth', label: string) => (
    <label className={styles.field}>
      <span>{label}</span>
      <select className={styles.select} value={value[key]} onChange={(e) => set({ [key]: Number(e.target.value) })}>
        {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
      </select>
    </label>
  );
  const current = PATTERNS.find((p) => p.id === value.pattern) ?? PATTERNS[0];

  return (
    <section className={styles.intake} aria-labelledby="intake-pattern-title">
      <div className={styles.head}>
        <h4 id="intake-pattern-title" className={styles.title}>How uploads arrive</h4>
        <div className={styles.patterns} role="radiogroup" aria-labelledby="intake-pattern-title">
          {PATTERNS.map((p) => (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={value.pattern === p.id}
              className={`${styles.pattern} ${value.pattern === p.id ? styles.active : ''}`}
              onClick={() => set({ pattern: p.id })}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>
      <p className={styles.hint}>{current.hint}</p>
      <div className={styles.fields}>
        {value.pattern === 'varying' && numField('quietPct', 'Quietest month', '% of the busiest', { max: 100, step: 5 })}
        {value.pattern === 'seasonal' && (
          <>
            {monthField('peakMonth', 'Busiest month')}
            {numField('swingPct', 'Swing ±', '% around the average', { max: 100, step: 5 })}
            {monthField('startMonth', 'Starts in')}
          </>
        )}
        {value.pattern === 'growing' && (
          <>
            {numField('rampStartPct', 'Start at', '% of the table', { max: 100, step: 5 })}
            {numField('rampMonths', 'Full volume by month', '', { min: 2, max: 120 })}
          </>
        )}
        {value.pattern === 'bursts' && (
          <>
            {numField('burstMonths', 'Busy for', 'months', { min: 1, max: 120 })}
            {numField('burstEvery', 'every', 'months', { min: 1, max: 120 })}
          </>
        )}
        {value.pattern === 'capped' && (
          <>
            {numField('storageCapGb', 'Storage limit', 'GB of originals', { step: 50 })}
            {plans.length > 0 && (
              <div className={styles.plans}>
                <span className={styles.suffix}>Plan quotas:</span>
                {plans.map((p) => (
                  <button
                    key={p.name}
                    type="button"
                    className={`${styles.planBtn} ${Math.abs(p.gb - value.storageCapGb) < 0.5 ? styles.active : ''}`}
                    onClick={() => set({ storageCapGb: Math.round(p.gb) })}
                  >
                    {p.name} · {gbLabel(p.gb)}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
        {numField('deleteAfterMonths', 'Delete files after', 'months (0 = keep them)', { max: 120 })}
      </div>
      {weights && weights.length > 0 && <IntakeStrip weights={weights} quiet={quietPct != null ? quietPct / 100 : null} />}
    </section>
  );
};

export default IntakePattern;
